import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Sparkles, CheckCircle } from 'lucide-react';
import { UpliftLogoWhite, UpliftShield } from '../components/UpliftLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * IMPROVED HERO SECTION PREVIEW
 * 
 * Changes from current design:
 * 1. Replaced colored circles with subtle mesh gradient background
 * 2. Added floating geometric shapes for depth (subtle, not distracting)
 * 3. Improved visual hierarchy with better typography scaling
 * 4. Enhanced spacing for better readability
 * 5. Made CTA more prominent with glow effect
 * 6. Added mini feature highlights below CTA
 * 7. Cleaner, more premium look
 * 8. Fully responsive
 * 9. No external images (CSS only for fast loading)
 */

const HeroPreview = () => {
  const [subscriberCount, setSubscriberCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscribers/count`);
        if (res.ok) {
          const data = await res.json();
          setSubscriberCount(data.count);
        }
      } catch (error) {
        // Non-critical: subscriber count fetch failed, use default
        console.debug('Subscriber count fetch failed:', error.message);
      }
    };
    fetchCount();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="uplift-logo">
              <UpliftLogoWhite size="sm" showText={true} />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-400 hover:text-white">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-500/25">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Premium Background */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Subtle Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        
        {/* Mesh Gradient Overlay - Subtle, premium feel */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 50% -20%, rgba(99, 102, 241, 0.15), transparent),
              radial-gradient(ellipse 60% 40% at 80% 50%, rgba(99, 102, 241, 0.08), transparent),
              radial-gradient(ellipse 60% 40% at 20% 80%, rgba(16, 185, 129, 0.06), transparent)
            `
          }}
        />

        {/* Subtle Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px'
          }}
        />

        {/* Floating Accent Lines - Subtle geometric elements */}
        <div className="absolute top-1/4 left-10 w-px h-32 bg-gradient-to-b from-transparent via-indigo-500/20 to-transparent hidden lg:block" />
        <div className="absolute bottom-1/4 right-10 w-px h-32 bg-gradient-to-b from-transparent via-emerald-500/20 to-transparent hidden lg:block" />
        <div className="absolute top-1/3 right-20 w-24 h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent hidden lg:block" />
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center pt-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm mb-8">
            <Sparkles className="w-4 h-4" />
            <span>AI-Powered Tools for Your Success</span>
          </div>

          {/* Shield Logo - Smaller, more refined */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <UpliftShield size={80} color="#6366F1" />
              {/* Subtle glow behind shield */}
              <div className="absolute inset-0 blur-2xl bg-indigo-500/20 -z-10" />
            </div>
          </div>
          
          {/* Main Headline - Better hierarchy */}
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Uplift Your{' '}
            <span className="relative">
              <span className="relative z-10 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Life
              </span>
            </span>
          </h1>
          
          {/* Subheadline - Clearer value prop */}
          <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-4 max-w-2xl mx-auto font-light">
            Three AI tools to transform your career, business, and finances.
          </p>
          
          {/* Supporting text */}
          <p className="text-base text-slate-500 mb-10 max-w-xl mx-auto">
            Resume builder • Business plans • Credit repair
          </p>

          {/* CTA Section - More prominent */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-indigo-600 hover:bg-indigo-500 text-lg px-8 py-6 h-auto shadow-xl shadow-indigo-500/30 hover:shadow-indigo-500/40 transition-all duration-300 hover:scale-105"
              >
                Start Free Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="#products">
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 h-auto border-slate-700 text-slate-300 hover:bg-white/5 hover:border-slate-600"
              >
                See How It Works
              </Button>
            </Link>
          </div>

          {/* Trust Indicators - Compact row */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>Instant access</span>
            </div>
            {subscriberCount > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-400" />
                <span>{subscriberCount}+ users joined</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom fade gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
      </section>
    </div>
  );
};

export default HeroPreview;
